/**
 * Check-in, check-out and manual task time logs all write one TimeTracking row per
 * ticket assignee, carrying identical data apart from `assignee`. The rows of one
 * cohort share a `session_id`, and the edit endpoint updates the whole cohort at
 * once, so the UI has to present a cohort as a single entry.
 *
 * Structurally typed rather than typed against a generated model, because the two
 * apps import TimeTracking from different generated clients.
 */

export type TimeEntryRow = {
  id?: string;
  session_id?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  ticket_item?: string | null;
  assignee?: number | null;
};

export type TimeEntryGroup<Row extends TimeEntryRow> = {
  key: string;
  // Null for legacy rows written before session_id existed. The edit endpoint has no
  // addressable cohort for those and rejects them with incorrect_format.
  sessionId: string | null;
  rows: Row[];
  // Times and check-in/check-out narrative are identical across a cohort, so the
  // first row stands in for all of them.
  entry: Row;
  assigneeIds: number[];
  durationSeconds: number | null;
  // Row ids are uuid7, so they sort lexicographically into creation order. Useful as
  // an ordering that an edit cannot move, unlike started_at. Row ids are also the
  // right key rather than session_id: the backfill minted session_ids at migration
  // time, so legacy cohorts would all sort together under it.
  sortKey: string;
};

function durationOf(row: TimeEntryRow): number | null {
  if (!row.started_at || !row.ended_at) {
    return null;
  }
  return (new Date(row.ended_at).getTime() - new Date(row.started_at).getTime()) / 1000;
}

/**
 * Groups on `session_id ?? id`, deliberately mirroring `session_id or row.id` in the
 * backend's time_tracking/totals.py. The list and the derived total_time must agree on
 * what counts as one session, or a legacy row would render as one entry while being
 * summed as several. Incoming order is preserved (the API returns -started_at).
 */
export function groupTimeEntries<Row extends TimeEntryRow>(
  rows: Row[]
): TimeEntryGroup<Row>[] {
  const rowsByKey = new Map<string, Row[]>();

  for (const row of rows) {
    const key = row.session_id ?? row.id;
    if (!key) {
      continue;
    }
    const existing = rowsByKey.get(key);
    if (existing) {
      existing.push(row);
    } else {
      rowsByKey.set(key, [row]);
    }
  }

  return [...rowsByKey.entries()].map(([key, cohortRows]) => {
    // Every row of a cohort carries the same started_at, so the API's ordering leaves
    // them in an arbitrary order relative to each other. Sorting by id makes both the
    // representative row and the technician listing stable between fetches.
    const orderedRows = [...cohortRows].sort((a, b) =>
      (a.id ?? "").localeCompare(b.id ?? "")
    );
    const entry = orderedRows[0];
    return {
      key,
      sessionId: entry.session_id ?? null,
      rows: orderedRows,
      entry,
      assigneeIds: orderedRows
        .map((row) => row.assignee)
        .filter((assignee) => assignee != null),
      durationSeconds: durationOf(entry),
      sortKey: entry.id ?? key,
    };
  });
}

/**
 * Check-in sessions and manual task logs are independent systems that happen to share
 * a table, and they are never summed together. Splitting client side is not a
 * shortcut: `?ticket_item=` can ask for one task's logs but cannot ask for rows with
 * no task, and the store already holds every row for the ticket.
 */
export function splitTimeEntries<Row extends TimeEntryRow>(
  rows: Row[]
): { sessions: TimeEntryGroup<Row>[]; taskLogs: TimeEntryGroup<Row>[] } {
  const groups = groupTimeEntries(rows);
  return {
    sessions: groups.filter((group) => group.entry.ticket_item == null),
    taskLogs: groups.filter((group) => group.entry.ticket_item != null),
  };
}
